import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AudioLines,
  PhoneCall,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCallActions } from '@/hooks/useCallActions'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import { InlineRecordingPlayer } from '@/components/manager/history/InlineRecordingPlayer'
import type { DrilldownCallRow } from '@/types/analyticsDrilldown'
import { displayPhone, formatDateTime, formatDuration } from './formatters'

interface Props {
  rows: DrilldownCallRow[]
  /** Affiche un bouton "Rappeler" en bout de ligne (utile pour `missed`). */
  showCallAction?: boolean
  /** Affiche la colonne Statut — utile pour `calls_total` / `missed` où les
   *  statuts sont hétérogènes. Inutile sur inbound/outbound (tous completed). */
  showStatus?: boolean
  companyId: number
  locale: string
  darkMode: boolean
}

// Mapping de statut → tonalité visuelle. Aligné sur la sémantique CallLog.
const STATUS_TONE: Record<string, { fg: string; bg: string }> = {
  completed: {
    fg: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-100 dark:bg-emerald-500/20',
  },
  missed: {
    fg: 'text-rose-700 dark:text-rose-300',
    bg: 'bg-rose-100 dark:bg-rose-500/20',
  },
  'no-answer': {
    fg: 'text-rose-700 dark:text-rose-300',
    bg: 'bg-rose-100 dark:bg-rose-500/20',
  },
  busy: {
    fg: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-100 dark:bg-amber-500/20',
  },
  failed: {
    fg: 'text-rose-700 dark:text-rose-300',
    bg: 'bg-rose-100 dark:bg-rose-500/20',
  },
  canceled: {
    fg: 'text-slate-600 dark:text-slate-400',
    bg: 'bg-slate-100 dark:bg-slate-700',
  },
  ringing: {
    fg: 'text-indigo-700 dark:text-indigo-300',
    bg: 'bg-indigo-100 dark:bg-indigo-500/20',
  },
  'in-progress': {
    fg: 'text-indigo-700 dark:text-indigo-300',
    bg: 'bg-indigo-100 dark:bg-indigo-500/20',
  },
}

function isMissed(row: DrilldownCallRow): boolean {
  return row.direction === 'inbound' && (row.status === 'missed' || row.status === 'no-answer')
}

function CallIcon({ row }: { row: DrilldownCallRow }) {
  if (isMissed(row)) return <PhoneMissed className="h-4 w-4 text-rose-500" />
  if (row.direction === 'inbound')
    return <PhoneIncoming className="h-4 w-4 text-emerald-500" />
  if (row.direction === 'outbound')
    return <PhoneOutgoing className="h-4 w-4 text-indigo-500" />
  return <PhoneCall className="h-4 w-4 text-slate-400" />
}

export function CallsDrilldownTable({
  rows,
  showCallAction,
  showStatus,
  companyId,
  locale,
  darkMode,
}: Props) {
  const { t } = useTranslation()
  const { makeOutboundCall } = useCallActions()
  const selectedCallerId = useSelectedCallerId()
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const headerBase = cn(
    'px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide',
    darkMode ? 'text-slate-400' : 'text-slate-500',
  )
  const cellBase = cn(
    'px-3 py-3 text-sm tabular-nums',
    darkMode ? 'text-slate-200' : 'text-slate-700',
  )
  const rowBorder = darkMode ? 'border-slate-700/60' : 'border-slate-200/70'

  // Nombre total de colonnes pour le colSpan de la ligne expanded.
  let baseColCount = 6 // icon + date + contact + agent + duration + recording
  if (showStatus) baseColCount += 1
  const totalCols = showCallAction ? baseColCount + 1 : baseColCount

  return (
    <div
      className={cn(
        'rounded-xl border overflow-hidden',
        darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className={cn(darkMode ? 'bg-slate-800/80' : 'bg-slate-50')}>
            <tr className={cn('border-b', rowBorder)}>
              <th className={cn(headerBase, 'pl-4 w-12')}></th>
              <th className={headerBase}>{t('manager.analytics.drilldown.calls.date')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.calls.peer')}</th>
              <th className={headerBase}>{t('manager.analytics.drilldown.calls.agent')}</th>
              {showStatus && (
                <th className={headerBase}>{t('manager.analytics.drilldown.calls.status')}</th>
              )}
              <th className={cn(headerBase, 'text-right')}>
                {t('manager.analytics.drilldown.calls.duration')}
              </th>
              <th className={cn(headerBase, 'text-center')}>
                {t('manager.analytics.drilldown.calls.recording')}
              </th>
              {showCallAction && <th className={cn(headerBase, 'text-right pr-4')}></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const peerNumber = row.peer_number || row.from_number
              const peerLabel = row.contact?.name || displayPhone(peerNumber)
              const phoneToCall = row.contact?.phone || peerNumber
              const canCall =
                showCallAction && phoneToCall && !phoneToCall.startsWith('client:')
              const isExpanded = expandedId === row.id

              return (
                <Fragment key={row.id}>
                  <tr
                    className={cn(
                      'border-b transition-colors',
                      rowBorder,
                      isExpanded
                        ? darkMode
                          ? 'bg-indigo-500/10'
                          : 'bg-indigo-50/60'
                        : darkMode
                          ? 'hover:bg-slate-800/60'
                          : 'hover:bg-slate-50',
                    )}
                  >
                    <td className={cn(cellBase, 'pl-4')}>
                      <CallIcon row={row} />
                    </td>
                    <td className={cellBase}>{formatDateTime(row.date, locale)}</td>
                    <td className={cellBase}>
                      <div className="flex flex-col">
                        <span className={cn(darkMode ? 'text-white' : 'text-slate-900', 'font-medium')}>
                          {peerLabel}
                        </span>
                        {row.contact && (
                          <span className={cn('text-xs', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                            {row.contact.phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={cellBase}>
                      {row.answered_by ? (
                        row.answered_by.name
                      ) : isMissed(row) && row.rung_agents.length > 0 ? (
                        <span className={cn('text-xs italic', darkMode ? 'text-slate-500' : 'text-slate-400')}>
                          {t('manager.analytics.drilldown.calls.rangAt', {
                            names: row.rung_agents
                              .filter(Boolean)
                              .map((a) => a!.name)
                              .join(', '),
                          })}
                        </span>
                      ) : (
                        <span className={cn(darkMode ? 'text-slate-500' : 'text-slate-400')}>–</span>
                      )}
                    </td>
                    {showStatus && (
                      <td className={cellBase}>
                        {(() => {
                          const tone = STATUS_TONE[row.status] || {
                            fg: 'text-slate-700 dark:text-slate-300',
                            bg: 'bg-slate-100 dark:bg-slate-700',
                          }
                          return (
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                tone.bg,
                                tone.fg,
                              )}
                            >
                              {t(`manager.analytics.drilldown.calls.statuses.${row.status}`, row.status)}
                            </span>
                          )
                        })()}
                      </td>
                    )}
                    <td className={cn(cellBase, 'text-right')}>
                      {formatDuration(row.duration_seconds)}
                    </td>
                    <td className={cn(cellBase, 'text-center')}>
                      {row.has_recordings ? (
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : row.id)}
                          title={t('manager.analytics.drilldown.calls.playRecording')}
                          className={cn(
                            'inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors',
                            isExpanded
                              ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30'
                              : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-500/20',
                          )}
                        >
                          <AudioLines className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <span className={cn('text-xs', darkMode ? 'text-slate-600' : 'text-slate-300')}>
                          –
                        </span>
                      )}
                    </td>
                    {showCallAction && (
                      <td className={cn(cellBase, 'text-right pr-4')}>
                        {canCall ? (
                          <button
                            type="button"
                            onClick={() =>
                              void makeOutboundCall(phoneToCall, selectedCallerId || undefined)
                            }
                            className={cn(
                              'inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium',
                              'transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                              'bg-indigo-500 text-white hover:bg-indigo-600',
                            )}
                          >
                            <PhoneCall className="h-3 w-3" />
                            {t('manager.analytics.drilldown.calls.callBack')}
                          </button>
                        ) : (
                          <span className={cn('text-xs', darkMode ? 'text-slate-600' : 'text-slate-300')}>
                            –
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr
                      className={cn(
                        'border-b',
                        rowBorder,
                        darkMode ? 'bg-indigo-500/5' : 'bg-indigo-50/40',
                      )}
                    >
                      <td colSpan={totalCols} className="px-3 py-2">
                        <div className="flex items-center">
                          <InlineRecordingPlayer
                            companyId={companyId}
                            callId={row.id}
                            onClose={() => setExpandedId(null)}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
